import { Component } from 'react';
import '../../../../styles/components/about-us.scss';
import {
  Binoculars,
  Goal,
  Handshake,
  Repeat,
  ShoppingCart,
  Trash2,
  Users,
  Building2,
  Package,
  Gavel,
} from 'lucide-react';
import HeaderBanner from '../../../common/HeaderBanner';
import i18n from '../../../../i18n';
import getAboutCounts from '../../../../models/about';

class AboutUs extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTimeline: null,
      stats: [
        {
          id: 1,
          value: 0,
          target: 0,
          label: i18n.t('active_buyers'),
          icon: Users,
          key: 'active_buyers',
        },
        {
          id: 2,
          value: 0,
          target: 0,
          label: i18n.t('active_clients'),
          icon: Building2,
          key: 'active_clients',
        },
        { id: 3, value: 0, target: 0, label: i18n.t('lots_sold'), icon: Package, key: 'lots_sold' },
        { id: 4, value: 0, target: 0, label: i18n.t('aucitons'), icon: Gavel, key: 'auctions' },
      ],
      hasAnimated: false,
      isLoading: true,
    };
    this.statsRef = null;
  }

  async componentDidMount() {
    window.scrollTo(0, 0);
    await this.getCounts();
    window.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  async getCounts() {
    try {
      let response = await getAboutCounts();
      if (response.success) {
        let { data } = response;

        // Update stats with API data
        this.setState((prevState) => ({
          stats: prevState.stats.map((stat) => ({
            ...stat,
            target: data[stat.key] || 0,
          })),
          isLoading: false,
        }));
      }
    } catch (error) {
      console.error('Error fetching counts:', error);
      this.setState({ isLoading: false });
    }
  }

  handleScroll = () => {
    if (this.statsRef && !this.state.hasAnimated && !this.state.isLoading) {
      const rect = this.statsRef.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom >= 0;

      if (isVisible) {
        this.setState({ hasAnimated: true });
        this.animateStats();
      }
    }
  };

  animateStats = () => {
    const { stats } = this.state;
    const duration = 2000; // 2 seconds
    const steps = 60;
    const stepDuration = duration / steps;

    stats.forEach((stat, index) => {
      if (stat.target === 0) return; // Skip animation if target is 0

      let currentStep = 0;
      const increment = stat.target / steps;

      const timer = setInterval(() => {
        currentStep++;
        const newValue = Math.min(Math.floor(increment * currentStep), stat.target);

        this.setState((prevState) => ({
          stats: prevState.stats.map((s, i) => (i === index ? { ...s, value: newValue } : s)),
        }));

        if (currentStep >= steps) {
          clearInterval(timer);
        }
      }, stepDuration);
    });
  };

  handleTimelineHover = (index) => {
    this.setState({ activeTimeline: index });
  };

  handleTimelineLeave = () => {
    this.setState({ activeTimeline: null });
  };

  render() {
    const { activeTimeline, stats } = this.state;

    const principles = [
      {
        id: 1,
        title: i18n.t('our_mission'),
        icon: Goal,
        description: i18n.t('our_mission_info'),
      },
      {
        id: 2,
        title: i18n.t('our_vision'),
        icon: Binoculars,
        description: i18n.t('our_vision_info'),
      },
      {
        id: 3,
        title: i18n.t('our_values'),
        icon: Handshake,
        description: i18n.t('our_values_info'),
      },
    ];

    const bestOffers = [
      {
        id: 1,
        title: i18n.t('online_auction'),
        icon: ShoppingCart,
        description: i18n.t('online_auction_info'),
      },
      {
        id: 2,
        title: i18n.t('waste_management'),
        icon: Trash2,
        description: i18n.t('waste_management_info'),
      },
      {
        id: 3,
        title: i18n.t('recycling'),
        icon: Repeat,
        description: i18n.t('recycling_info'),
      },
    ];

    const journeyData = [
      {
        id: 1,
        year: '2020',
        title: i18n.t('the_idea'),
        description: i18n.t('the_idea_info'),
        position: 'right',
      },
      {
        id: 2,
        year: '2021',
        title: i18n.t('platform_launch'),
        description: i18n.t('platform_launch_info'),
        position: 'left',
      },
      {
        id: 3,
        year: '2023',
        title: i18n.t('million_users'),
        description: i18n.t('million_users_info'),
        position: 'right',
      },
    ];

    const teamMembers = [
      {
        id: 1,
        name: i18n.t('suleym_junaibi'),
        position: i18n.t('chairman'),
        image: '/assets/images/about/chairman.jpg',
      },
      {
        id: 2,
        name: i18n.t('ibrahim_junaibi'),
        position: i18n.t('ceo'),
        image: '/assets/images/about/ceo.jpg',
      },
      {
        id: 3,
        name: i18n.t('ahmed_alsaadi'),
        position: i18n.t('gm'),
        image: '/assets/images/about/gm.png',
      },
    ];

    return (
      <div className="about-us-page">
        <HeaderBanner title={i18n.t('about_mzadcom')} subtitle={i18n.t('about_mzadcom_subtitle')} />

        {/* Core Principles Section */}
        <section className="principles-section">
          <div className="container-l2">
            <h2 className="section-title">{i18n.t('our_core_principles')}</h2>
            <div className="principles-grid">
              {principles.map((principle) => {
                const Icon = principle.icon;
                return (
                  <div key={principle.id} className="principle-card">
                    <div className="principle-icon">
                      <Icon size={40} color="#1b8f9b" />
                    </div>
                    <h3 className="principle-title">{principle.title}</h3>
                    <p className="principle-description">{principle.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Journey Timeline Section */}
        <section className="journey-section">
          <div className="container-l2">
            <h2 className="section-title">{i18n.t('our_journey')}</h2>
            <div className="timeline-l2">
              <div className="timeline-l2-line"></div>
              {journeyData.map((item, index) => (
                <div
                  key={item.id}
                  className={`timeline-l2-item ${item.position} ${
                    activeTimeline === index ? 'active' : ''
                  }`}
                  onMouseEnter={() => this.handleTimelineHover(index)}
                  onMouseLeave={this.handleTimelineLeave}
                >
                  <div className="timeline-l2-marker">
                    <span className="timeline-l2-number">{item.id}</span>
                  </div>
                  <div className="timeline-l2-content">
                    <div className="timeline-l2-year">{item.year}</div>
                    <h3 className="timeline-l2-title">{item.title}</h3>
                    <p className="timeline-l2-description">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* Stats Section - Our Story in Numbers */}
        <section className="stats-l2-section" ref={(ref) => (this.statsRef = ref)}>
          <div className="stats-l2-background">
            <div className="stats-l2-overlay"></div>
          </div>
          <div className="container-l2">
            <h2 className="section-title stats-l2-title">{i18n.t('our_story_in_numbers')}</h2>

            <div className="stats-l2-grid">
              {stats.map((stat) => {
                const Icon = stat.icon;
                return (
                  <div key={stat.id} className="stat-l2-card">
                    <div className="stat-l2-icon">
                      <Icon size={36} />
                    </div>
                    <div className="stat-l2-value">{stat.value.toLocaleString()}</div>
                    <div className="stat-l2-label">{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
        {/* Our Best Section */}
        <section className="principles-section">
          <div className="container-l2">
            <h2 className="section-title">{i18n.t('we_offer_best_service')}</h2>
            <div className="principles-grid">
              {bestOffers.map((offer) => {
                const Icon = offer.icon;
                return (
                  <div key={offer.id} className="principle-card">
                    <div className="principle-icon">
                      <Icon size={40} color="#1b8f9b" />
                    </div>
                    <h3 className="principle-title">{offer.title}</h3>
                    <p className="principle-description">{offer.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
        {/* CTA Section */}
        <section className="cta-section">
          <div className="cta-background">
            <div className="cta-particles">
              {[...Array(15)].map((_, i) => (
                <div key={i} className={`cta-particle cta-particle-${i + 1}`}></div>
              ))}
            </div>
          </div>
          <div className="container-l2">
            <div className="cta-content">
              <h2 className="cta-title">{i18n.t('about_section_cta_title')}</h2>
              <p className="cta-description">{i18n.t('about_section_cta_description')}</p>
              {/* <button className="cta-btn">Join Mzadcom</button> */}
            </div>
          </div>
        </section>
        {/* Team Section */}
        <section className="team-section">
          <div className="container-l2">
            <h2 className="section-title">{i18n.t('meet_our_team')}</h2>
            <div className="team-grid">
              {teamMembers.map((member) => (
                <div key={member.id} className="team-card">
                  <div className="team-image">
                    <div className="team-avatar">
                      <img src={member.image} alt={member.name} />
                    </div>
                  </div>
                  <h3 className="team-name">{member.name}</h3>
                  <p className="team-position">{member.position}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }
}

export default AboutUs;
